// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // Add token state
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in on initial load
    const storedToken = sessionStorage.getItem('authToken');
    const userDetails = sessionStorage.getItem('userDetails');

    if (storedToken && userDetails) {
      setIsAuthenticated(true);
      setToken(storedToken); // Set token state from sessionStorage
      try {
        setUser(JSON.parse(userDetails));
      } catch (error) {
        console.error('Failed to parse user details:', error);
      }
    }

    setLoading(false);
    
    // Add listener for profile image updates
    const handleProfileUpdate = (event) => {
      if (event.detail) {
        // Update user with the new profile data
        updateUser(event.detail);
      }
    };

    // Add listener for subscription updates
    const handleSubscriptionUpdate = () => {
      // Refresh user data from sessionStorage
      refreshUserFromStorage();
    };
    
    // Listen for the custom events
    document.addEventListener('user-profile-updated', handleProfileUpdate);
    document.addEventListener('user-subscription-updated', handleSubscriptionUpdate);
    
    // Clean up event listeners
    return () => {
      document.removeEventListener('user-profile-updated', handleProfileUpdate);
      document.removeEventListener('user-subscription-updated', handleSubscriptionUpdate);
    };
  }, []);

  const login = (result) => {
    sessionStorage.setItem('authToken', result.token);
    sessionStorage.setItem('userDetails', JSON.stringify(result.userDataResponse));
    sessionStorage.setItem('accountStatus', result.userDataResponse.accountStatus);
    sessionStorage.setItem('eddf8f1d483e321cd92650ced4397958', result.userDataResponse.isPaymentOverdue);

    setToken(result.token); // Set token state
    setIsAuthenticated(true);
    setUser(result.userDataResponse);
  };

  const logout = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userDetails');
    sessionStorage.removeItem('accountDetails');
    sessionStorage.removeItem('accountStatus');

    setToken(null); // Clear token state
    setIsAuthenticated(false);
    setUser(null);

    // Redirect to login page
    navigate('/login');
  };

  const updateUser = (updatedUserData) => {
    // Update the user state
    setUser(prevUser => {
      if (!prevUser) return updatedUserData;
      
      const newUser = {
        ...prevUser,
        ...updatedUserData
      };
      
      // Update sessionStorage
      try {
        sessionStorage.setItem('userDetails', JSON.stringify(newUser));
      } catch (error) {
        console.error('Failed to update user in sessionStorage:', error);
      }
      
      return newUser;
    });
  };

  // New method to refresh user data from sessionStorage
  const refreshUserFromStorage = () => {
    const userDetails = sessionStorage.getItem('userDetails');
    if (userDetails) {
      try {
        const parsedUser = JSON.parse(userDetails);
        setUser(parsedUser);
      } catch (error) {
        console.error('Failed to parse user details from sessionStorage:', error);
      }
    }
  };

  // Method to manually trigger user refresh (can be called from components)
  const refreshUser = () => {
    refreshUserFromStorage();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token, // Include token in the context value
        loading,
        login,
        logout,
        updateUser,
        refreshUser // Add this method
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);